
import { defineComponent, ref, computed } from "vue";
import axios from "@/axios-instance";
import moment from "moment";
import { gradWarningsOf } from "@/core/helpers/gradCheck";

export default defineComponent({
  name: "candidate-quick-drawer",
  props: {
    // Query gắn vào link "Mở trang chi tiết" — vd "?from=lienthong" để breadcrumb trang chi tiết
    // quay về đúng danh sách nguồn (Danh sách ứng viên liên thông)
    detailQuery: { type: String, default: "" },
  },
  setup() {
    const visible = ref(false);
    const loading = ref(false);
    const d = ref<any>({});
    // Winner + trạng thái nhập học của SV (từ applicant/student/:id) — cho badge "đã trúng tuyển / nhập học"
    const winners = ref<any[]>([]);

    // Mở drawer + nạp đầy đủ thông tin ứng viên (cá nhân + học tập)
    const open = async (id: string | number) => {
      visible.value = true;
      loading.value = true;
      d.value = {};
      winners.value = [];
      await axios
        .get(`/admission/student/${id}`)
        .then(({ data }) => {
          d.value = data || {};
        })
        .catch(() => {
          d.value = {};
        });
      loading.value = false;
      // Nạp trạng thái xét tuyển/nhập học riêng (không chặn hiển thị drawer — badge hiện ngay sau khi có)
      axios
        .get(`/applicant/student/${id}`)
        .then(({ data }) => {
          winners.value = Array.isArray(data) ? data : [];
        })
        .catch(() => {
          winners.value = [];
        });
    };

    // Bảng điểm học bạ (view-only) — key môn khớp admission_forms.subjectthpt
    const transcriptSubjects = [
      { label: "Toán", value: "mathTHPT" },
      { label: "Ngữ văn", value: "literatureTHPT" },
      { label: "Tiếng Anh", value: "englishTHPT" },
      { label: "Vật lý", value: "physicsTHPT" },
      { label: "Hóa học", value: "chemistryTHPT" },
      { label: "Sinh học", value: "biologicalTHPT" },
      { label: "Lịch sử", value: "historyTHPT" },
      { label: "Địa lý", value: "geographyTHPT" },
      { label: "GDCD", value: "civicEducationTHPT" },
      { label: "Giáo dục kinh tế và pháp luật", value: "economicsAndLawEducationTHPT" },
      { label: "Tin học", value: "informationTechnologyTHPT" },
      { label: "Vẽ", value: "artTHPT" },
      { label: "Tiếng Pháp", value: "frenchTHPT" },
    ];
    const transcriptYears = [
      { key: "c10", label: "Lớp 10" },
      { key: "c11", label: "Lớp 11" },
      { key: "c12", label: "Lớp 12" },
    ];
    // Chỉ hiện môn có ít nhất 1 điểm — hàng toàn "—" là nhiễu trong khung xem nhanh
    const scoredSubjects = computed(() => {
      const t = d.value?.transcript;
      if (!t || typeof t !== "object") return [];
      return transcriptSubjects.filter((sub) =>
        transcriptYears.some((y) => t[sub.value]?.[y.key] != null)
      );
    });
    // Điểm thi tốt nghiệp THPT (graduationscore — 1 điểm/môn, 10 môn khớp form PT1)
    const gradScoreSubjects = [
      { label: "Toán", value: "mathTHPT" },
      { label: "Ngữ văn", value: "literatureTHPT" },
      { label: "Tiếng Anh", value: "englishTHPT" },
      { label: "Vật lý", value: "physicsTHPT" },
      { label: "Hóa học", value: "chemistryTHPT" },
      { label: "Sinh học", value: "biologicalTHPT" },
      { label: "Lịch sử", value: "historyTHPT" },
      { label: "Địa lý", value: "geographyTHPT" },
      { label: "Tin học", value: "informationTechnologyTHPT" },
      { label: "GDKT & PL", value: "economicsAndLawEducationTHPT" },
    ];
    const scoredGradSubjects = computed(() => {
      const g = d.value?.graduationscore;
      if (!g || typeof g !== "object") return [];
      return gradScoreSubjects.filter((sub) => g[sub.value] != null && g[sub.value] !== "");
    });

    const fmtDate = (v: any) => (v ? moment(v).format("DD/MM/YYYY") : "—");
    const joinParts = (arr: any[]) => {
      const s = (arr || []).filter((x) => x != null && String(x).trim() !== "").join(", ");
      return s || "—";
    };
    //Cảnh báo tốt nghiệp (cùng logic banner trang chi tiết + row vàng list)
    const gradWarns = computed(() => gradWarningsOf(d.value));

    // Hồ sơ LIÊN THÔNG: BE trả d._arpros khi student.isarticulationprogram -> hiện bằng CĐ/TC thay THPT
    const arpro = computed(() => d.value?._arpros || {});
    const isLt = computed(() => !!d.value?._arpros);
    const trainingFormLabel = (v: string) => ({ chinhquy: 'Chính quy', nghe: 'Nghề' } as any)[v] || (v || '—');

    // Badge xét tuyển: có winner chưa hủy = đã trúng tuyển; có decisionCode = đã cấp QĐ, chưa có = chờ QĐ.
    const admissionBadge = computed(() => {
      const ws = (winners.value || []).filter((w) => w.status !== "cancel");
      if (!ws.length) return null;
      return ws.some((w) => w.decisionCode)
        ? { label: "Đã trúng tuyển", state: "success" }
        : { label: "Chờ QĐ trúng tuyển", state: "warning" };
    });
    // Badge nhập học theo applicant.status: confirmed = đã hoàn tất nhập học; processing/completed = đang nhập học.
    const enrollBadge = computed(() => {
      const ss = (winners.value || []).map((w) => w.applicantStatus).filter(Boolean);
      if (ss.includes("confirmed")) return { label: "Đã nhập học", state: "success" };
      if (ss.some((s) => s === "processing" || s === "completed")) return { label: "Đang nhập học", state: "info" };
      return null;
    });

    return { visible, loading, d, open, fmtDate, joinParts, transcriptYears, scoredSubjects, scoredGradSubjects, gradWarns, admissionBadge, enrollBadge, isLt, arpro, trainingFormLabel };
  },
});
